/* ============================================================
   SurvMaps Mobile gate — desktop-only landing page shown to
   phone visitors. Ported from "SurvMaps phone mode/app.jsx".
   ============================================================ */
import React, { useState, useEffect, useRef, useCallback } from "react";
import "./MobileGate.css";

/* FastAPI endpoint that streams the SurvPA pamphlet PDF. */
const runtimeBackendBase =
  typeof window !== "undefined" && window.__BACKEND_URL__ ? window.__BACKEND_URL__ : "";
const PAMPHLET_ENDPOINT = `${runtimeBackendBase}/api/survpa/pamphlet`;

/* Subtle drifting-particles canvas layered over the hero art. */
function BackgroundFX() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = canvas.getContext("2d");
    let raf, w, h, dpr;
    const N = 34;
    let parts = [];

    const seed = () => {
      parts = Array.from({ length: N }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.4 + 0.4,
        vy: -(Math.random() * 0.18 + 0.05),
        vx: (Math.random() - 0.5) * 0.12,
        a: Math.random() * 0.5 + 0.15,
        tw: Math.random() * Math.PI * 2,
      }));
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of parts) {
        p.x += p.vx;
        p.y += p.vy;
        p.tw += 0.03;
        if (p.y < -4) { p.y = h + 4; p.x = Math.random() * w; }
        if (p.x < -4) p.x = w + 4;
        if (p.x > w + 4) p.x = -4;
        const tw = (Math.sin(p.tw) + 1) / 2;
        const alpha = p.a * (0.4 + 0.6 * tw);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(123,170,252,${alpha})`;
        ctx.shadowColor = "rgba(74,113,228,0.9)";
        ctx.shadowBlur = 6;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener("resize", resize);
    if (!reduce) tick();
    else { ctx.clearRect(0, 0, w, h); }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="fx-canvas" aria-hidden="true" />;
}

function Hero() {
  return (
    <header className="hero" data-screen-label="Mobile gate">
      <BackgroundFX />
      <div className="scanlines" aria-hidden="true"></div>
      <div className="scan-sweep" aria-hidden="true"></div>

      <div className="hero-body">
        <div className="hero-copy">
          <div className="topbar">
            <div className="brand">
              <span className="brand-mark" aria-hidden="true"><span></span><span></span><span></span></span>
              <span className="brand-name">SurvMaps</span>
            </div>
            <span className="status-pill"><span className="status-dot"></span>solo&nbsp;desktop</span>
          </div>

          <h1 className="headline">Per non farvi diventare <em>ciechi</em>...</h1>
          <p className="subhead">
            SurvMaps è disponibile solo su Desktop. Niente da fare su smartphone.
          </p>
        </div>

        <a className="scroll-hint" href="#survpa">
          <span>Anzi aspetta, qualcosa da fare c'è</span>
          <span className="chev" aria-hidden="true">▾</span>
        </a>
      </div>
    </header>
  );
}

function Promo() {
  const [state, setState] = useState("idle");

  const download = useCallback(() => {
    setState("loading");
    setTimeout(() => setState("idle"), 2600);
  }, []);

  return (
    <section id="survpa" className="promo" data-screen-label="SurvPA promo">
      <img className="promo-logo" src="/survpa-logo.png" alt="SurvPA" />

      <h2 className="promo-title">Vi ricordate di quando i software non erano a noleggio?</h2>

      <div className="promo-body">
        <p>
          SurvPA è un software per PRG per pubbliche amministrazioni
          (quel PA non sta lì a caso…) pensato per essere scaricabile dalla cittadinanza.
        </p>
      </div>

      <ul className="features">
        <li><span className="tick">✓</span><span>Estrae PDF con tutte le info del PRG che riguardano il tuo immobile</span></li>
        <li><span className="tick">✓</span><span>Produce DXF con la sovrapposizione tra immobile, catasto e PRG</span></li>
        <li><span className="tick">✓</span><span>Ricerca immobili per indirizzo e per particella catastale</span></li>
      </ul>

      <div className="promo-body promo-body--after-features">
        <p>
          Funziona anche offline. SurvPA è la soluzione risparmia tempo per il tuo Comune.
          Spargi la voce! (puoi provarlo su survmaps.it su Demo PA)
        </p>
        <p>
          Qui sotto trovi un depliant PDF con tutti i vantaggi del software.
        </p>
      </div>

      <div className="dl-wrap">
        <a
          className={"btn-dl" + (state === "loading" ? " loading" : "")}
          href={PAMPHLET_ENDPOINT}
          download
          onClick={download}
        >
          {state === "loading"
            ? <><span className="spin" aria-hidden="true"></span><span>Preparazione del PDF…</span></>
            : <><span className="ic" aria-hidden="true">▾</span><span>Scarica il depliant (PDF)</span></>}
        </a>
      </div>

      <footer className="foot">
        <span>SurvPA · desktop GIS</span>
        <span>v1.0</span>
      </footer>
    </section>
  );
}

export default function MobileGate() {
  return (
    <div className="survmaps-mobile-gate">
      <div className="app">
        <Hero />
        <Promo />
      </div>
    </div>
  );
}
